import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { BinaRepository } from '@data-layer/repositories';
import { delay, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { BinaOptionsModel, BinaPartialOptionsModel, BinaTreeModel } from '@data-layer/models/common';
import { UtilsHelper } from '@helpers/utils-helper';
import {
  BINA_EMPTY_GROUP,
  BINA_FIFTH_GROUP_NUM,
  BINA_FIRST_GROUP_NUM,
  BINA_FOURTH_GROUP_NUM,
  BINA_SECOND_GROUP_NUM,
  BINA_THIRD_GROUP_NUM,
} from '../const';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { Router } from '@angular/router';

@Component({
  selector: 'bina-sidebar',
  templateUrl: './bina-sidebar.component.html',
  styleUrls: ['./bina-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BinaSidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public existingHTS: string;
  @Input() public existingDescription: string;
  @Input() public isChangeToBinaTitleHeader: boolean = false;
  @Input() public isShowInsertCode: boolean = true;
  @Input() public isShowClassifierEndOptions: boolean = false;
  @Input() public isShowAddToCatalog: boolean;
  @Input() public importerId?: number;
  @Input() public manufacturerId?: number;
  @Output() public htsFound: EventEmitter<{ hts: string; description: string }> = new EventEmitter<{ hts: string; description: string }>();
  @Output() public addHtsToCatalog: EventEmitter<{ hts: string; description: string }> = new EventEmitter<{
    hts: string;
    description: string;
  }>();
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();

  public termControl: UntypedFormControl;

  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public isClassifying: boolean;
  public isLoading: boolean;
  public isChildLoading: boolean;
  public isFooterHover: boolean;
  public isShowBinaTitleHeader: boolean;
  public isAutoFocus: boolean;

  public treeData: BinaTreeModel[] = [];
  public helperData: BinaTreeModel[] = [];
  public joinedGroups: BinaTreeModel[] = [];
  public initialHtsIds: string[] = [];
  public selectedHTS: string[] = [];

  @ViewChild('scrollingContainer', { static: false })
  public scrollingContainer: ElementRef;
  @ViewChild('termControlInput')
  public termControlInput: ElementRef;

  public scrollIt$: ReplaySubject<void> = new ReplaySubject<void>();
  public recalcTree$: ReplaySubject<void> = new ReplaySubject<void>();
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private companiesService: CompaniesService,
    private binaRepository: BinaRepository,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.termControl = new UntypedFormControl();

    this.joinedGroupsCalcListener();
    this.scrollToBottomListener();

    if (!!this.existingDescription?.length) {
      this.termControl.setValue(this.existingDescription.trim());
    }
    if (!!this.existingHTS?.length) {
      this.termControl.setValue(this.existingHTS.trim());
    }
    if (!!this.termControl.value?.length) {
      this.classify();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngAfterViewInit(): void {
    if (this.isAutoFocus && !!this.termControlInput) {
      setTimeout(() => {
        this.termControlInput?.nativeElement.focus();
      }, 100);
    }
  }

  public a(): void {
    this.cdr.markForCheck();
  }

  public classify(): void {
    if (!this.termControl.value) {
      return;
    }

    if (this.isChangeToBinaTitleHeader) {
      this.showBinaTitleHeader(true);
    }

    this.treeData = [];
    this.helperData = [];
    this.selectedHTS = [];
    this.recalcTree$.next();
    this.isClassifying = true;
    this.isLoading = true;

    this.getClassification(this.termControl.value)
      .pipe(
        tap((data: BinaOptionsModel) => {
          (data?.response || []).forEach((firstClassification: { [key: string]: number }) => {
            // Convert to array of {hts, confidence} pairs and sort by confidence descending
            const htsWithConfidence = Object.entries(firstClassification)
              .map(([hts, confidence]) => ({
                hts,
                confidence: typeof confidence === 'number' ? confidence : parseFloat(String(confidence)) || 0,
              }))
              .sort((a, b) => b.confidence - a.confidence); // Sort descending by confidence

            // Push sorted items to treeData, preserving confidence
            this.treeData.push(...htsWithConfidence.map((item) => ({ hts: item.hts, confidence: item.confidence })));
          });
        }),
        switchMap(() => {
          const apiCalls: Array<Observable<BinaPartialOptionsModel>> = this.treeData.map((item: BinaTreeModel) =>
            this.getInnerOptions(item.hts),
          );

          return apiCalls.length ? forkJoin(apiCalls) : of([]);
        }),
        tap((options: BinaPartialOptionsModel[]) => {
          for (let i: number = 0; i < this.treeData.length; i++) {
            this.treeData[i] = {
              ...this.treeData[i],
              hts: this.formatHTSCode(this.treeData[i]?.hts, options[i]?.children),
              children: options[i]?.children,
              description: options[i]?.description || '',
            };
          }

          this.selectedHTS = this.treeData.map((item: BinaTreeModel) => item.hts);
          this.initialHtsIds = [...this.selectedHTS];
        }),
        switchMap(() => {
          const apiCalls: Array<Observable<BinaPartialOptionsModel[]>> = this.treeData.map((item: BinaTreeModel) =>
            this.reverseHTSGetData(item.hts),
          );
          return apiCalls.length ? forkJoin(apiCalls) : of([]);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((helperData: BinaPartialOptionsModel[][]) => {
        helperData.forEach((item: BinaPartialOptionsModel[]) => this.helperData.push(...item));
        this.recalcTree$.next();
        this.cdr.markForCheck();
      });
  }

  public fullDescription(htsCode: string): string {
    return this.joinedGroups
      .filter(
        (item: BinaTreeModel) =>
          htsCode.startsWith(item?.hts) && (htsCode.length > BINA_FIRST_GROUP_NUM ? item?.hts?.length > BINA_FIRST_GROUP_NUM : true),
      )
      .map((item: BinaTreeModel) => item.description)
      .filter((description: string) => !!description)
      .join(' => ');
  }

  public hint(htsCode: string): string {
    if (htsCode.length === BINA_FIFTH_GROUP_NUM) {
      return;
    }

    if (htsCode.length <= BINA_FIRST_GROUP_NUM) {
      return 'BINA_HINT_STEP_1';
    }

    if (htsCode.length === BINA_SECOND_GROUP_NUM) {
      return 'BINA_HINT_STEP_2';
    }

    if (htsCode.length === BINA_THIRD_GROUP_NUM) {
      return 'BINA_HINT_STEP_3';
    }

    if (htsCode.length === BINA_FOURTH_GROUP_NUM) {
      return 'BINA_HINT_STEP_4';
    }
  }

  public handleChild(htsCode: string): void {
    this.treeData = this.treeData.filter((item: BinaTreeModel) => htsCode.startsWith(item.hts) && item.hts.length < htsCode.length);

    const existingItem: BinaTreeModel = this.joinedGroups.find((item: BinaTreeModel) => item.hts === htsCode);
    if (!existingItem) {
      this.isChildLoading = true;
      this.getInnerOptions(htsCode)
        .pipe(
          tap((data: BinaPartialOptionsModel) => this.treeData.push({ ...data, hts: this.formatHTSCode(htsCode, data?.children) })),
          tap(() => (this.selectedHTS = this.treeData.map((item: BinaTreeModel) => item.hts))),
          finalize(() => (this.isChildLoading = false)),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.recalcTree$.next();
          this.scrollIt$.next();
          this.cdr.markForCheck();
        });
    } else {
      this.treeData.push(existingItem);
      this.selectedHTS = this.treeData.map((item: BinaTreeModel) => item.hts);
      this.recalcTree$.next();
      this.scrollIt$.next();
    }
  }

  public handlePath(htsCode: string): void {
    this.treeData = [];
    this.selectedHTS = [];
    this.initialHtsIds = [];
    this.isLoading = true;

    this.reverseHTSGetData(htsCode)
      .pipe(
        tap((items: BinaPartialOptionsModel[]) => {
          // #10450
          items.forEach((data: BinaPartialOptionsModel) => {
            if (!!data.description) {
              this.treeData.push(data);
            }
          });

          this.selectedHTS = this.treeData.map((item: BinaTreeModel) => item.hts);
        }),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.recalcTree$.next();
        this.cdr.markForCheck();
      });
  }

  public onHtsInsert(hts: string): void {
    this.htsFound.emit({ hts, description: this.termControl.value });
  }

  public onAddHtsToCatalog(hts: string): void {
    this.addHtsToCatalog.emit({ hts, description: this.termControl.value });
  }

  public classifyInputPressKey(e: KeyboardEvent): void {
    const enterKey: string = 'Enter';
    if (e.code === enterKey) {
      this.classify();
    }
  }

  public showBinaTitleHeader(value: boolean) {
    this.isShowBinaTitleHeader = value;
  }

  public footerOver(): void {
    this.isFooterHover = !!this.joinedGroups.find((item: BinaTreeModel) => item.hts?.length === BINA_FIFTH_GROUP_NUM);
    if (this.isFooterHover) {
      this.scrollIt$.next();
    }
  }

  public footerOut(): void {
    this.isFooterHover = false;
  }

  public isShowPath(hts: string): boolean {
    return (
      (hts.length > BINA_FIRST_GROUP_NUM && hts.length < BINA_FIFTH_GROUP_NUM && this.initialHtsIds.includes(hts)) ||
      hts.length === BINA_FIFTH_GROUP_NUM
    );
  }

  public onClassifyAnotherItem() {
    this.scrollingContainer.nativeElement.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
    this.showBinaTitleHeader(false);
    setTimeout(() => {
      this.termControlInput.nativeElement.focus();
    }, 100);
  }

  public onFileEntrySummary() {
    this.router.navigate(['/entry-summary/start']);
  }

  public get isShowEmptyResult(): boolean {
    return !Object.keys(this.treeData || {})?.length && !this.isLoading;
  }

  public get isTreeHasAnyFullHTS(): boolean {
    if (!this.treeData.length) return;
    return this.treeData.some((item) => item.hts.length === BINA_FIFTH_GROUP_NUM);
  }

  private getClassification(term: string): Observable<BinaOptionsModel> {
    return this.binaRepository.getClassification(`${term}`, this.importerId, this.manufacturerId);
  }

  private getInnerOptions(partialHtsCode: string): Observable<BinaPartialOptionsModel> {
    return partialHtsCode.length <= BINA_FOURTH_GROUP_NUM
      ? this.getOptions(partialHtsCode)
      : this.getDescription(partialHtsCode).pipe(map((description: string) => ({ description, children: {} })));
  }

  private getOptions(partialHtsCode: string): Observable<BinaPartialOptionsModel> {
    return this.binaRepository.getOptions(partialHtsCode);
  }

  private getDescription(partialHtsCode: string): Observable<string> {
    return this.binaRepository.getDescription(partialHtsCode);
  }

  private reverseHTSGetData(htsCode: string): Observable<BinaPartialOptionsModel[]> {
    const htsDataCalls: Array<Observable<BinaPartialOptionsModel>> = this.reverseHTS(htsCode).map((item: string) =>
      this.getInnerOptions(item).pipe(
        map((optionData: BinaPartialOptionsModel) => ({ ...optionData, hts: this.formatHTSCode(item, optionData?.children) })),
      ),
    );

    return forkJoin(htsDataCalls);
  }

  private reverseHTS(htsCode: string): string[] {
    const result: string[] = [];

    if (!htsCode) {
      return [];
    }

    if (htsCode.length >= BINA_FIRST_GROUP_NUM) {
      result.push(this.codeGroup(htsCode, 0, BINA_FIRST_GROUP_NUM));
    }
    if (htsCode.length >= BINA_SECOND_GROUP_NUM) {
      result.push(this.codeGroup(htsCode, 0, BINA_SECOND_GROUP_NUM));
    }
    if (htsCode.length >= BINA_THIRD_GROUP_NUM) {
      result.push(this.codeGroup(htsCode, 0, BINA_THIRD_GROUP_NUM));
    }
    if (htsCode.length >= BINA_FOURTH_GROUP_NUM) {
      result.push(this.codeGroup(htsCode, 0, BINA_FOURTH_GROUP_NUM));
    }
    if (htsCode.length === BINA_FIFTH_GROUP_NUM) {
      result.push(this.codeGroup(htsCode, 0, BINA_FIFTH_GROUP_NUM));
    }

    return result;
  }

  private codeGroup(htsCode: string, indexA: number, indexB: number): string {
    return htsCode?.substring(indexA, indexB);
  }

  private formatHTSCodeToString(htsToFormat: string | number): string {
    let hts: string = htsToFormat?.toString();
    if (hts.length === 1) {
      hts = `0${hts}`;
    }

    return hts;
  }

  private isHasEmptyFullHts(hts: string, childKeys: string[]): boolean {
    return (
      hts.length < BINA_FIFTH_GROUP_NUM &&
      childKeys.length === 1 &&
      childKeys[0].length === BINA_FIFTH_GROUP_NUM &&
      childKeys[0].substring(BINA_FIFTH_GROUP_NUM - 2, BINA_FIFTH_GROUP_NUM) === BINA_EMPTY_GROUP
    );
  }

  private formatHTSCode(htsToFormat: string | number, children: { [key: string]: string }): string {
    const hts: string = this.formatHTSCodeToString(htsToFormat);
    const childKeys: string[] = Object.keys(children) || [];

    return !this.isHasEmptyFullHts(hts, childKeys) ? hts : childKeys[0];
  }

  private joinedGroupsCalc(): BinaTreeModel[] {
    return UtilsHelper.uniqBy<BinaTreeModel>([...this.treeData, ...this.helperData], 'hts').sort(
      (a: BinaTreeModel, b: BinaTreeModel) => a?.hts?.length - b?.hts?.length,
    );
  }

  private joinedGroupsCalcListener(): void {
    this.recalcTree$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.joinedGroups = this.joinedGroupsCalc();
      this.cdr.markForCheck();
    });
  }

  private scrollToBottomListener(): void {
    this.scrollIt$.pipe(delay(100), takeUntil(this.destroy$)).subscribe(() => {
      if (!this.scrollingContainer || !this.scrollingContainer.nativeElement) {
        return;
      }
      const scrollHeight: number = this.scrollingContainer.nativeElement.scrollHeight || 0;

      this.scrollingContainer.nativeElement.scrollTo({
        behavior: 'smooth',
        top: scrollHeight,
      });
    });
  }
}
